import React, { useState } from 'react';
import { CharacterCard, AddCardPlaceholder, AddCharacterModal, EditCharacterModal } from '../components';
import { useCharacters } from '../hooks';
import Skeleton from '../components/common/Skeleton';

/**
 * Konten halaman Character/Card List dengan grid responsif
 * Data diambil dari API https://api.cloudnine.biz.id/characters.php
 */
const CardListContent = ({ isDarkMode, onSelectCharacter }) => {
    const { characters, loading, error, refetch, addCharacter, updateCharacter, uploadImage } = useCharacters();
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingCharacter, setEditingCharacter] = useState(null);

    const handleCardClick = (character) => {
        if (onSelectCharacter) {
            onSelectCharacter(character);
        }
    };

    const handleAddSubmit = async (characterData) => {
        const result = await addCharacter(characterData);
        return !!result;
    };

    const handleEditSubmit = async (characterData) => {
        const result = await updateCharacter(characterData);
        return !!result;
    };

    return (
        <div className="space-y-4 pb-20 md:pb-0 animate-fade-in">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Character Collection</h2>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded transition-colors shadow-sm"
                >
                    + New Character
                </button>
            </div>

            {/* Tier Legend */}
            <div className={`
                flex flex-wrap gap-2 p-3 rounded-lg text-[10px]
                ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'}
            `}>
                <span className={`${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Tiers:</span>
                <span className="text-gray-500">● Common</span>
                <span className="text-emerald-500">● Uncommon</span>
                <span className="text-blue-500">● Rare</span>
                <span className="text-purple-500">● Epic</span>
                <span className="text-yellow-500">● Legend</span>
                <span className="text-red-500">● Mythical</span>
                <span className="bg-gradient-to-r from-cyan-500 via-white to-fuchsia-500 bg-clip-text text-transparent font-bold">● Divine</span>
            </div>

            {/* Error State */}
            {error && !loading && (
                <div className={`
                    flex flex-col items-center justify-center py-12 rounded-lg gap-3
                    ${isDarkMode ? 'bg-rose-900/10 border border-rose-800/30' : 'bg-rose-50 border border-rose-200'}
                `}>
                    <p className="text-xs text-rose-500">Failed to load characters: {error}</p>
                    <button
                        onClick={refetch}
                        className="text-xs bg-rose-500 hover:bg-rose-600 text-white px-3 py-1.5 rounded transition-colors"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Character Grid */}
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8 gap-4">
                {/* Loading Skeletons */}
                {loading && [...Array(12)].map((_, i) => (
                    <div key={i} className={`relative aspect-[3/4] rounded-xl overflow-hidden p-2 flex flex-col justify-end ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                        <Skeleton className="absolute inset-0 w-full h-full opacity-50" isDarkMode={isDarkMode} />
                        <div className="relative z-10 w-full">
                            <Skeleton className="h-4 w-3/4 mb-2 rounded" isDarkMode={isDarkMode} />
                            <Skeleton className="h-3 w-1/2 rounded" isDarkMode={isDarkMode} />
                        </div>
                    </div>
                ))}

                {/* Real Cards */}
                {!loading && !error && characters.map((character) => (
                    <CharacterCard
                        key={character.id}
                        character={character}
                        isDarkMode={isDarkMode}
                        onClick={handleCardClick}
                        onEdit={(char) => setEditingCharacter(char)}
                    />
                ))}

                {!loading && !error && (
                    <AddCardPlaceholder
                        isDarkMode={isDarkMode}
                        onClick={() => setShowAddModal(true)}
                        label="Add Character"
                    />
                )}
            </div>

            {/* Empty State */}
            {!loading && !error && characters.length === 0 && (
                <div className={`
                    flex flex-col items-center justify-center py-16 rounded-lg
                    ${isDarkMode ? 'bg-slate-800/30' : 'bg-slate-50'}
                `}>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        No characters yet
                    </p>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded transition-colors"
                    >
                        + Add your first character
                    </button>
                </div>
            )}

            {/* Add Character Modal */}
            <AddCharacterModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSubmit={handleAddSubmit}
                onUploadImage={uploadImage}
                isDarkMode={isDarkMode}
            />

            {/* Edit Character Modal */}
            <EditCharacterModal
                isOpen={!!editingCharacter}
                onClose={() => setEditingCharacter(null)}
                onSubmit={handleEditSubmit}
                onUploadImage={uploadImage}
                character={editingCharacter}
                isDarkMode={isDarkMode}
            />
        </div>
    );
};

export default CardListContent;
